import { Fragment } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Users, ShieldOff, Megaphone } from "lucide-react";

import { api } from "@/lib/api";
import { contactHref } from "@/lib/v3People";
import { findConv, chatRoute, convTitle, convContext, isDirect, normalizeMessages } from "@/lib/v3Messages";
import { useMemberAuth } from "../MemberAuthContext";
import { useCore } from "../useCore";
import { requiredCollection } from "../requiredCollection";
import { ConnectionError } from "./ConnectionError";
import { Avatar, Loading } from "./kit";
import { V3Title, V3Columns, V3Actions, StatusBanner, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "./v3";
import { AreaCard, ResponsibleCard, V3_CARD } from "./v3app";

// V3 building blocks for the message views (M17, M77–M81, M119).
export const useConversations = () => useCore("/member/conversations", (d) => requiredCollection(d, "conversations"));
export const messagesPath = (c) => `/member/conversations/${encodeURIComponent(c.id)}/messages`;

// Fresh reads for the send/report checks; `ok: false` when the Core could not be read.
export async function readMessages(c) {
  try {
    const r = await api.get(messagesPath(c));
    const list = normalizeMessages(r.data);
    return list ? { ok: true, list } : { ok: false, list: null };
  } catch {
    return { ok: false, list: null };
  }
}

export async function readConversation(id) {
  try {
    const r = await api.get("/member/conversations");
    return { ok: true, conv: findConv(requiredCollection(r.data, "conversations"), id) };
  } catch {
    return { ok: false, conv: null };
  }
}

export const ChatAside = ({ text: body, title }) => <><AreaCard title={title} text={body} testid="chat-area" /><ResponsibleCard /></>;

export function ChatHead({ c }) {
  const direct = isDirect(c);
  const Icon = c.type === "broadcast" ? Megaphone : Users;
  return (
    <div className={`${V3_CARD} mb-[17px] flex min-h-[73px] items-center gap-[15px] px-[14px] py-[12px]`} data-testid="chat-head">
      {direct ? <Avatar name={c.other?.name} color={c.other?.avatar_color} size="h-[42px] w-[42px] text-[13px]" />
        : <span aria-hidden="true" className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full bg-v3-lilac text-v3-magenta"><Icon className="h-[20px] w-[20px]" /></span>}
      <div className="min-w-0">
        <p className="break-words text-[18px] font-bold leading-[24px] text-v3-ink" data-testid="chat-head-name">{convTitle(c)}</p>
        <p className={`mt-[4px] text-[12.5px] leading-[17px] ${direct ? "text-v3-green" : "text-v3-muted"}`} data-testid="chat-head-context">{convContext(c)}</p>
      </div>
    </div>
  );
}

// No visible conversation (foreign, removed, declined): nothing about it is shown.
export function ChatMissing() {
  const navigate = useNavigate();
  const back = () => (window.history.length > 1 ? navigate(-1) : navigate("/nachrichten"));
  return (
    <div data-testid="chat-missing">
      <V3Title title="Gespräch nicht verfügbar" intro="Dieses Gespräch ist für deinen Zugang nicht freigegeben oder besteht nicht mehr." testid="chat-missing-title" />
      <V3Columns layout="detail" aside={<ChatAside text="Kein Gespräch ausgewählt" />} main={<>
        <StatusBanner tone="sand" icon={ShieldOff} title="Kein Zugriff" text="Gespräch nicht sichtbar oder nicht vorhanden" testid="chat-missing-banner" />
        <DetailList>
          <DetailRow label="Gespräch" value="Nicht sichtbar oder nicht vorhanden" testid="chat-missing-conv" />
          <DetailRow label="Inhalte" value="Werden nicht angezeigt" testid="chat-missing-content" />
          <DetailRow label="Nächster Schritt" value="Zur Übersicht der Nachrichten zurückkehren" testid="chat-missing-next" />
        </DetailList>
        <V3Actions>
          <Link to="/nachrichten" data-testid="chat-missing-list" className={V3_PRIMARY}>Zu den Nachrichten</Link>
          <button type="button" onClick={back} data-testid="chat-missing-back" className={V3_SECONDARY}>Zurück</button>
        </V3Actions>
      </>} />
    </div>
  );
}

// Loads the conversation from GET /member/conversations; open contact requests go to their contact views.
export function WithChat({ children }) {
  const { member } = useMemberAuth();
  const { id } = useParams();
  const res = useConversations();
  if (res.error) return <ConnectionError onRetry={res.reload} />;
  if (!res.data) return <Loading />;
  const c = findConv(res.data, id);
  const route = chatRoute(c);
  if (route === "incoming" || route === "outgoing") return <Navigate to={contactHref(route, c.id)} replace />;
  if (route !== "chat") return <ChatMissing />;
  return <Fragment key={`${member?.id || "anon"}:${c.id}`}>{children(c)}</Fragment>;
}
